import { Area } from './../../node_modules/react-easy-crop/types.d';
export const omitEmpty = (params: Record<string, unknown>) =>
  Object.keys(params)
    .filter((k) => params[k] !== "" && params[k] !== undefined)
    .reduce((c, v) => ({ ...c, [v]: params[v] }), {});

export function fileToBlob(file: File) {
  if (file)
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const blob = new Blob([reader.result as ArrayBuffer], {
          type: file.type,
        });
        resolve(blob);
      };

      reader.onerror = () => {
        reject(new Error("Failed to convert file to Blob"));
      };

      reader.readAsArrayBuffer(file);
    });
  else {
    return "";
  }
}

export const validateEmail = (email: string) => {
  return email.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
};

export function isValidUrl(url: string) {
  try {
    new URL(url);
    return true;
  } catch (err) {
    return false;
  }
}

export function generateCode(length = 6) {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return result;
}

export const getCroppedImg = async (
  imageSrc: string,
  croppedAreaPixels: { width: number; height: number; x: number; y: number },
  rotation = 0,
  output = 'blob' // 'blob' | 'base64'
): Promise<Blob | string | null> => {
  const createImage = (url: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.crossOrigin = 'anonymous'; // VERY IMPORTANT
      image.onload = () => resolve(image);
      image.onerror = (error) => reject(error);
      image.src = url;
    });

  const getRadianAngle = (degreeValue: number) => (degreeValue * Math.PI) / 180;

  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Could not get canvas context');
  }

  const radians = getRadianAngle(rotation);

  // calculate bounding box of rotated image
  const sin = Math.abs(Math.sin(radians));
  const cos = Math.abs(Math.cos(radians));
  const bBoxWidth = croppedAreaPixels.width * cos + croppedAreaPixels.height * sin;
  const bBoxHeight = croppedAreaPixels.width * sin + croppedAreaPixels.height * cos;

  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // move the origin to the center of canvas
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate(radians);
  ctx.translate(-croppedAreaPixels.x - croppedAreaPixels.width / 2, -croppedAreaPixels.y - croppedAreaPixels.height / 2);

  ctx.drawImage(image, 0, 0);

  return new Promise((resolve, reject) => {
    if (output === 'blob') {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'));
          return;
        }
        resolve(blob);
      }, 'image/png');
    } else {
      resolve(canvas.toDataURL('image/png'));
    }
  });
};

const DEFAULT_NUMBER_LOCALE = "en-US";

/**
 * Format number consistently across the app.
 * @param value - number to format
 * @param decimals - fixed number of decimal places. Leave empty for natural decimals.
 * @returns formatted string
 */
export const formatNumber = (value: number | string | null | undefined, decimals?: number): string => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === null || num === undefined || isNaN(num)) return '0';

  const fractionOptions =
    decimals === undefined
      ? { maximumFractionDigits: 2 }
      : {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        };

  return num.toLocaleString(DEFAULT_NUMBER_LOCALE, fractionOptions);
};

/**
 * Parse formatted number string to float
 * @param value - formatted number string
 * @returns parsed number
 */
export const parseFormattedNumber = (value: string): number => {
  // Remove all non-digit characters except dot and comma
  const cleaned = value.replace(/[^\d.,-]/g, '');
  // Replace comma with dot for parsing
  const normalized = cleaned.replace(',', '.');
  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? 0 : parsed;
};
